import React, { useState, useEffect } from 'react';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory } from '../utils/i18n';
import { 
  Table, Sparkles, ArrowLeft, Upload, FileText, Download, 
  Copy, Check, AlertCircle, RefreshCw, Calculator, FileCheck,
  Search, ShieldCheck, Layers, HelpCircle, CheckSquare, Coins
} from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import { LocalModelToggle } from './LocalModelToggle';

interface OfficeSpreadsheetAgentViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const DEMO_SPREADSHEET_DATA = `Дата,Стаття_витрат,КЕКВ,Сума_грн,Відділ,Статус
2026-07-02,Придбання офісного паперу та канцелярських приладь,2210,14500.00,Адміністрація,Оплачено
2026-07-05,Оплата послуг швидкісного оптоволоконного інтернету,2240,8200.00,IT Департамент,Оплачено
2026-07-08,Закупівля картриджів для тонерних принтерів,2210,22300.00,IT Департамент,Оплачено
2026-07-12,Повірка та технічне обслуговування лічильників води,2240,4600.00,Господарський,В обробці
2026-07-15,Придбання комплектів клавіатур та мишей для ПК,2210,18900.00,IT Департамент,Оплачено
2026-07-18,Послуги з юридичного та ліцензійного аудиту,2240,45000.00,Юридичний,Оплачено
2026-07-22,Закупівля господарських та миючих засобів,2210,9800.00,Господарський,Оплачено
2026-07-25,Передплата хмарних серверних потужностей (AWS/GCP),2240,38500.00,IT Департамент,Оплачено
2026-07-28,Придбання мережевих комутаторів 1Gbps,2210,31200.00,IT Департамент,Скасовано
2026-07-30,Закупівля дезінфікуючих та антисептичних засобів,2210,6400.00,Адміністрація,Оплачено`;

export default function OfficeSpreadsheetAgentView({
  language,
  onBack,
  onGoHome,
  useEnterpriseContext,
  enterpriseName
}: OfficeSpreadsheetAgentViewProps) {
  const isUk = language === 'uk';
  const agentInfo = agentsConfig.find(a => a.id === 'office-spreadsheet-agent');

  const [tableData, setTableData] = useState('');
  const [sumCondition, setSumCondition] = useState('');
  const [analysisGoal, setAnalysisGoal] = useState('');
  const [selectedOperation, setSelectedOperation] = useState('1. Умовне підсумовування комірок (SUMIF / SUMIFS)');
  const [useLocalModel, setUseLocalModel] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefer_local_model');
    return saved !== null ? saved === 'true' : true;
  });
  
  const [isFileExtracting, setIsFileExtracting] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    analysisMarkdown: string;
    summaryMetrics?: Record<string, any>;
  } | null>(null);

  useEffect(() => {
    if (result && result.analysisMarkdown) {
      autoDownloadReport(result.analysisMarkdown, 'Аналітик Таблиць Excel & CSV', 'office-spreadsheet-agent');
    }
  }, [result]);

  const [copiedMd, setCopiedMd] = useState(false);
  const [isGeneratingDocx, setIsGeneratingDocx] = useState(false);

  // Load pricing
  const [agentPricing, setAgentPricing] = useState<{ price: number; limit: number; singlePrice: number } | null>(null);
  useEffect(() => {
    fetch('/api/subscription/pricing')
      .then(res => res.json())
      .then(data => {
        if (data && data['office-spreadsheet-agent']) {
          setAgentPricing(data['office-spreadsheet-agent']);
        } else if (data && data['contract-auditor']) {
          setAgentPricing(data['contract-auditor']);
        }
      })
      .catch(() => {});
  }, []);

  const handleDemoFill = () => {
    setTableData(DEMO_SPREADSHEET_DATA);
    setSumCondition('Підсумувати всі значення в стовпці "Сума_грн" де "КЕКВ" = "2210" та "Статус" = "Оплачено"');
    setAnalysisGoal('Визначити Top-3 найбільші платежі, розрахувати частку витрат за КЕКВ 2210 у загальних оплачених витратах (у %) та сформувати структуровану зведену таблицю по відділах.');
    setSelectedOperation('1. Умовне підсумовування комірок (SUMIF / SUMIFS)');
    setError(null);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsFileExtracting(true);
    setError(null);
    setUploadedFileName(file.name);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/extract-text', {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || (isUk ? 'Помилка вилучення вмісту файлу.' : 'Failed to extract file text.'));
      }

      const data = await res.json();
      if (data.text) {
        setTableData(data.text);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Не вдалося обробити файл.' : 'Failed to process file.'));
      setUploadedFileName(null);
    } finally {
      setIsFileExtracting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tableData.trim()) {
      setError(isUk ? 'Будь ласка, введіть або завантажте табличні дані (CSV / Excel / Markdown).' : 'Please enter or upload spreadsheet data.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch('/api/office-spreadsheet-analyst', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tableData,
          sumCondition,
          analysisGoal,
          selectedOperation,
          useEnterpriseContext,
          enterpriseName,
          useLocalModel
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || (isUk ? 'Помилка аналізу таблиці.' : 'Spreadsheet analysis failed.'));
      }

      const data = await res.json();
      setResult({
        analysisMarkdown: data.analysisMarkdown,
        summaryMetrics: data.summaryMetrics
      });
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка при аналізі даних.' : 'An error occurred during data analysis.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyMd = () => {
    if (!result?.analysisMarkdown) return;
    navigator.clipboard.writeText(result.analysisMarkdown);
    setCopiedMd(true);
    setTimeout(() => setCopiedMd(false), 2500);
  };

  const handleDownloadMd = () => {
    if (!result?.analysisMarkdown) return;
    const blob = new Blob([result.analysisMarkdown], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Spreadsheet_Analysis_Report_${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadDocx = async () => {
    if (!result?.analysisMarkdown) return;
    setIsGeneratingDocx(true);
    try {
      const res = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: result.analysisMarkdown,
          fileName: `Звіт_Аналізу_Таблиці_ДСТУ_${Date.now()}`
        })
      });

      if (!res.ok) {
        throw new Error(isUk ? 'Помилка створення Word-документа.' : 'Failed to generate Word document.');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Звіт_Аналізу_Таблиці_ДСТУ_${Date.now()}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || (isUk ? 'Не вдалося завантажити Word-документ.' : 'Failed to download Word file.'));
    } finally {
      setIsGeneratingDocx(false);
    }
  };

  const isPl = language === 'pl';
  const isEn = language === 'en';

  return (
    <div className="space-y-6 animate-fade-in pb-16 font-mono">
      {/* Top Header Navigation */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            🏠 {isPl ? 'Strona Główna' : isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? '← Powrót do Centrum Biurowego' : isUk ? '← До Офісного Центру' : '← Back to Office Hub'}</span>
          </button>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <LocalModelToggle
            isLocal={useLocalModel}
            onToggle={(enabled) => {
              setUseLocalModel(enabled);
              localStorage.setItem('prefer_local_model', enabled ? 'true' : 'false');
            }}
            language={language}
          />
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-md">
            {isPl ? 'Subskrypcja 490 zł / 30 zapytań' : isUk ? '490 грн / 30 запитів' : '490 UAH / 30 req.'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-600 text-white rounded-md shadow-xs">
            {isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Run FREE'}
          </span>
        </div>
      </div>

      {/* Main Title & Hero Card */}
      <div className="bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute right-0 top-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-4 max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-full text-xs font-bold uppercase">
            <Table className="w-4 h-4 text-emerald-400" />
            <span>{isPl ? 'Analityk Arkuszy Excel & CSV & Sumowanie Warunkowe (SUMIF)' : isUk ? 'Аналітик Таблиць Excel & CSV & Умовне Підсумовування (SUMIF)' : 'Spreadsheet Intelligence & SUMIF Agent'}</span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-white flex items-center gap-3">
            <span>📊 {agentInfo ? getLocalizedAgentName(agentInfo, language) : (isPl ? 'Analityk Arkuszy Excel & CSV' : isUk ? 'Аналітик Таблиць Excel & CSV' : 'AI Excel & CSV Spreadsheet Analyst')}</span>
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Automatyczne wykonywanie sumowania warunkowego (SUMIF/SUMIFS), wyszukiwania (VLOOKUP/XLOOKUP), wykrywania duplikatów i anomalii w plikach Excel (.xlsx), CSV, TSV. Generuje profesjonalny raport w formatach Markdown (.md) oraz Word (.docx).'
              : isUk 
              ? 'Автоматичне виконання умовного підсумовування (SUMIF/SUMIFS), вибірки комірок (VLOOKUP/XLOOKUP), пошуку дублікатів, аномалій та обчислення агрегованих метрик у таблицях Excel (.xlsx), CSV, TSV. Формує професійний аналітичний звіт у форматах Markdown (.md) та Word (.docx) за ДСТУ 4163:2020.'
              : 'Automated conditional cell summation (SUMIF/SUMIFS), targeted VLOOKUP/XLOOKUP extraction, anomaly detection, and data aggregation across Excel (.xlsx), CSV, and TSV spreadsheets. Generates DSTU 4163:2020 Word (.docx) & Markdown (.md) reports.'}
          </p>

          <div className="pt-2 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={handleDemoFill}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-emerald-600/30 flex items-center gap-2 cursor-pointer hover:scale-[1.02]"
            >
              <Sparkles className="w-4 h-4 animate-pulse text-amber-300" />
              <span>{isPl ? '⚡ Wypróbuj dane demo 1 kliknięciem' : isUk ? '⚡ Спробувати на демо-прикладі в 1 клік' : '⚡ Try Demo Example in 1-Click'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Input Form */}
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-6 shadow-sm">
        
        {/* Step 1: Input Data / Upload */}
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-xs font-extrabold uppercase text-slate-900 flex items-center gap-2">
              <Table className="w-4 h-4 text-emerald-600" />
              <span>1. Табличні дані (Введіть, Вставте або Завантажте файл .xlsx / .csv / .txt)</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="file"
                id="file-upload"
                accept=".xlsx,.xls,.csv,.tsv,.json,.txt,.doc,.docx,.pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
              <label
                htmlFor="file-upload"
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 border border-slate-300"
              >
                <Upload className="w-3.5 h-3.5 text-slate-600" />
                <span>{isFileExtracting ? (isUk ? 'Зчитування...' : 'Parsing...') : (isUk ? '📁 Завантажити файл' : '📁 Upload File')}</span>
              </label>
            </div>
          </div>

          {uploadedFileName && (
            <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-bold text-emerald-800 flex items-center justify-between">
              <span>📄 Завантажено файл: {uploadedFileName}</span>
              <button
                type="button"
                onClick={() => { setUploadedFileName(null); setTableData(''); }}
                className="text-slate-500 hover:text-red-600 cursor-pointer text-xs"
              >
                ✕ Очистити
              </button>
            </div>
          )}

          <textarea
            value={tableData}
            onChange={(e) => setTableData(e.target.value)}
            rows={10}
            placeholder={isUk 
              ? "Вставте вміст таблиці у форматі CSV, TSV, Markdown (| Комірка |) або звичайний текст з роздільниками...\nПриклад:\nДата,Стаття,Сума_грн,Статус\n2026-07-01,Папір,15000,Оплачено"
              : "Paste spreadsheet contents in CSV, TSV, or Markdown format..."}
            className="w-full p-4 border border-slate-300 rounded-xl font-mono text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all bg-slate-50/50"
          />
        </div>

        {/* Step 2: Selected Operation (Combobox with Datalist) */}
        <div className="space-y-3">
          <label className="text-xs font-extrabold uppercase text-slate-900 flex items-center gap-2">
            <Calculator className="w-4 h-4 text-emerald-600" />
            <span>2. Вид операції або алгоритму аналізу (Оберіть зі списку або введіть власну)</span>
          </label>

          <input
            list="spreadsheet-operations"
            type="text"
            value={selectedOperation}
            onChange={(e) => setSelectedOperation(e.target.value)}
            placeholder="Виберіть зі списку підказок або введіть свій варіант..."
            className="w-full p-3.5 border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
          />
          <datalist id="spreadsheet-operations">
            <option value="1. Умовне підсумовування комірок (SUMIF / SUMIFS)" />
            <option value="2. Пошук та вибірка за критеріями (VLOOKUP / XLOOKUP)" />
            <option value="3. Аналіз дублікатів, аномалій та прогалин у даних" />
            <option value="4. Групування за категоріями та розрахунок часток (%)" />
            <option value="5. Розрахунок підсумкових показників (Min, Max, Avg, Median)" />
            <option value="6. Комплексний фінансово-категоріальний аудит таблиці" />
          </datalist>
        </div>

        {/* Step 3: Conditional Summing Input (SUMIF) */}
        <div className="space-y-2">
          <label className="text-xs font-extrabold uppercase text-slate-900 flex items-center gap-2">
            <Calculator className="w-4 h-4 text-blue-600" />
            <span>3. Умова підсумовування комірок (SUMIF / SUMIFS)</span>
          </label>
          <input
            type="text"
            value={sumCondition}
            onChange={(e) => setSumCondition(e.target.value)}
            placeholder={isUk 
              ? 'Наприклад: "Сумувати значення в колонці \'Сума_грн\' де \'КЕКВ\' = 2210 та \'Статус\' = \'Оплачено\'"' 
              : 'Example: "Sum column \'Amount\' where \'Category\' = \'IT\'"'}
            className="w-full p-3.5 border border-slate-300 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500 bg-white"
          />
        </div>

        {/* Step 4: Additional Analysis Goals */}
        <div className="space-y-2">
          <label className="text-xs font-extrabold uppercase text-slate-900 flex items-center gap-2">
            <Search className="w-4 h-4 text-indigo-600" />
            <span>4. Додаткові завдання аналізу, фільтрації та пошуку</span>
          </label>
          <textarea
            value={analysisGoal}
            onChange={(e) => setAnalysisGoal(e.target.value)}
            rows={3}
            placeholder={isUk 
              ? 'Вкажіть додаткові питання: знайти дублікати, вирахувати середняк, знайти найбільші значення, згрупувати по відділах...' 
              : 'Enter additional query goals: find duplicates, top-3 items, averages, grouping...'}
            className="w-full p-3.5 border border-slate-300 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500 bg-white"
          />
        </div>

        {/* Error Notification */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-xs font-bold text-red-600 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white font-extrabold text-sm uppercase rounded-xl transition-all shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer"
        >
          {isLoading ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>{isUk ? 'Обробка та Розрахунок Таблиці...' : 'Analyzing Spreadsheet...'}</span>
            </>
          ) : (
            <>
              <Table className="w-5 h-5" />
              <span>{isUk ? '📊 Обробити та Сформувати Аналітичний Звіт' : '📊 Process & Generate Analytics Report'}</span>
            </>
          )}
        </button>
      </form>

      {/* Results Section */}
      {result && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-6 shadow-lg animate-fade-in">
          
          {/* Result Actions Header */}
          <div className="flex items-center justify-between gap-4 flex-wrap border-b border-slate-200 pb-4">
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-emerald-600" />
              <h2 className="text-base font-extrabold text-slate-900 uppercase tracking-tight">
                {isUk ? 'Аналітичний Звіт та Розрахунки Таблиці' : 'Spreadsheet Analytics & Calculations Report'}
              </h2>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <TelegramExportButton
                title={isUk ? 'Аналіз Таблиць Excel & CSV' : 'Spreadsheet Analysis Report'}
                content={result.analysisMarkdown}
                agentName={isUk ? 'Аналітик Таблиць Excel' : 'Office Spreadsheet Analyst'}
                agentId="office-spreadsheet-analyst"
                isUk={isUk}
                variant="emerald"
              />

              <button
                onClick={handleCopyMd}
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors flex items-center gap-1.5 border border-slate-300 cursor-pointer"
              >
                {copiedMd ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                <span>{copiedMd ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Скопійовати MD' : 'Copy MD')}</span>
              </button>

              <button
                onClick={handleDownloadMd}
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors flex items-center gap-1.5 border border-slate-300 cursor-pointer"
              >
                <Download className="w-4 h-4" />
                <span>{isUk ? '📥 Завантажити .md' : '📥 Download .md'}</span>
              </button>

              <ExcelExportButton
                title={isUk ? 'Розрахункова Таблиця Excel' : 'Spreadsheet Calculation Table'}
                content={result.analysisMarkdown}
                fileName="office_spreadsheet_analysis"
                isUk={isUk}
                variant="primary"
              />

              <button
                onClick={handleDownloadDocx}
                disabled={isGeneratingDocx}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white font-extrabold text-xs uppercase rounded-xl transition-colors flex items-center gap-1.5 shadow-md border border-slate-700 cursor-pointer disabled:bg-slate-400"
              >
                {isGeneratingDocx ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                <span>{isUk ? '📄 Word (.docx)' : '📄 Word (.docx)'}</span>
              </button>
            </div>
          </div>

          {/* Markdown Content Display */}
          <div className="prose prose-slate max-w-none text-xs sm:text-sm text-slate-800 leading-relaxed font-mono bg-slate-50/50 p-6 rounded-xl border border-slate-200 overflow-x-auto">
            <div dangerouslySetInnerHTML={{ 
              __html: formatMarkdownHtml(result.analysisMarkdown) 
            }} />
          </div>

          {/* Bottom DSTU Footer Note */}
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-xs font-mono text-emerald-900 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 font-bold">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>{isUk ? 'Оформлено за ДСТУ 4163:2020 • Готово до друку та підпису' : 'Formatted per DSTU 4163:2020 standard'}</span>
            </div>
            <span className="text-[10px] text-emerald-700">Deep Research Hub • https://research.ai-assistent.online</span>
          </div>

        </div>
      )}
    </div>
  );
}

// Simple Markdown formatting helper for tables and headers
function formatMarkdownHtml(md: string): string {
  if (!md) return '';

  let html = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3 class="text-base font-extrabold text-slate-900 mt-4 mb-2 uppercase">$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2 class="text-lg font-extrabold text-emerald-800 mt-6 mb-3 border-b border-emerald-200 pb-1 uppercase">$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1 class="text-xl font-extrabold text-slate-900 mt-6 mb-4 uppercase">$1</h1>');

  // Bold and italic
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-extrabold text-slate-900">$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');

  // Code blocks / inline code
  html = html.replace(/`([^`]+)`/g, '<code class="bg-slate-200 text-emerald-800 px-1.5 py-0.5 rounded font-mono text-xs">$1</code>');

  // Tables converting
  const lines = html.split('\n');
  let inTable = false;
  let tableBuffer: string[] = [];
  let outLines: string[] = [];

  for (let line of lines) {
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableBuffer = [];
      }
      tableBuffer.push(line.trim());
    } else {
      if (inTable) {
        outLines.push(convertMdTableToHtml(tableBuffer));
        inTable = false;
        tableBuffer = [];
      }
      outLines.push(line);
    }
  }
  if (inTable) {
    outLines.push(convertMdTableToHtml(tableBuffer));
  }

  return outLines.join('<br />');
}

function convertMdTableToHtml(tableLines: string[]): string {
  if (tableLines.length === 0) return '';

  let html = '<div class="overflow-x-auto my-4"><table class="w-full border-collapse border border-slate-300 text-xs text-left">';

  tableLines.forEach((line, index) => {
    // Skip delimiter line (|---|---|)
    if (line.includes('---')) return;

    const cells = line.split('|').slice(1, -1).map(c => c.trim());
    if (index === 0) {
      html += '<thead class="bg-slate-100 font-extrabold text-slate-900 border-b-2 border-slate-300"><tr>';
      cells.forEach(cell => {
        html += `<th class="border border-slate-300 px-3 py-2 uppercase">${cell}</th>`;
      });
      html += '</tr></thead><tbody>';
    } else {
      html += '<tr class="hover:bg-slate-50/80 transition-colors">';
      cells.forEach(cell => {
        html += `<td class="border border-slate-300 px-3 py-2">${cell}</td>`;
      });
      html += '</tr>';
    }
  });

  html += '</tbody></table></div>';
  return html;
}
